import { applyAlpha, buildLinearGradient, humanizeToken, isDarkColor } from "./colorUtils";

const DEFAULT_LOGO = {
  uri: "https://customer-assets.emergentagent.com/job_dual-view-agent/artifacts/ls3jjxc1_noderiver_white.svg",
  alt: "BrandOn logo",
};

const DEFAULT_THEME = {
  brandName: "BrandOn",
  tagline: "Your on-brand AI partner",
  logo: DEFAULT_LOGO,
  gradients: {
    hero: "linear-gradient(135deg, #0B2433 0%, #03131d 100%)",
    cta: "linear-gradient(135deg, #1E8997 0%, #0e273a 100%)",
  },
  palette: {
    primary: "#1E8997",
    accent: "#DAFF35",
    dark: "#031a24",
    neutral: "#f4f7fb",
    text: "#0f172a",
    textOnPrimary: "#ffffff",
  },
  modes: {
    light: {
      background: "#f8fafc",
      surface: "#ffffff",
      text: "#0f172a",
      muted: "#6b7280",
      border: "#e5e7eb",
      input: "#ffffff",
      accent: "#1E8997",
      accentContrast: "#ffffff",
    },
    dark: {
      background: "#0b1220",
      surface: "#111827",
      text: "#f8fafc",
      muted: "rgba(255,255,255,0.78)",
      border: "rgba(255,255,255,0.18)",
      input: "#0f172a",
      accent: "#DAFF35",
      accentContrast: "#0b1220",
    },
    noderiver: {
      background: "linear-gradient(135deg, #0B2433 0%, #03131d 100%)",
      surface: "rgba(255,255,255,0.08)",
      text: "#ffffff",
      muted: "rgba(255,255,255,0.8)",
      border: "rgba(255,255,255,0.2)",
      input: "rgba(255,255,255,0.12)",
      accent: "#DAFF35",
      accentContrast: "#02121b",
    },
  },
};

const cloneTheme = (theme) => ({
  ...theme,
  logo: { ...theme.logo },
  gradients: { ...theme.gradients },
  palette: { ...theme.palette },
  modes: {
    light: { ...theme.modes.light },
    dark: { ...theme.modes.dark },
    noderiver: { ...theme.modes.noderiver },
  },
});

const pickPaletteColor = (palette, matcher) =>
  palette.find((item) => matcher((item.token ?? "").toLowerCase(), (item.role ?? "").toLowerCase()))
    ?.value;

const buildPrioritySelector = (palette, priorities) => {
  for (const predicate of priorities) {
    const match = palette.find(predicate);
    if (match?.value) {
      return match.value;
    }
  }
  return null;
};

const ensureReadableSurface = (color, fallback = "#f8fafc") => {
  if (!color) return fallback;
  return isDarkColor(color, 0.5) ? fallback : color;
};

const buildTagline = (visualSpec, brandProfileSpec) => {
  const candidates = [
    visualSpec?.meta?.description,
    Array.isArray(visualSpec?.meta?.keywords) && visualSpec.meta.keywords.length > 0
      ? visualSpec.meta.keywords.join(" • ")
      : null,
    humanizeToken(visualSpec?.meta?.differentiator),
    humanizeToken(brandProfileSpec?.meta?.differentiator),
    typeof brandProfileSpec?.brand?.industry === "string" ? brandProfileSpec.brand.industry : null,
    typeof brandProfileSpec?.brand?.name === "string" ? `${brandProfileSpec.brand.name} experience` : null,
  ];
  return candidates.find((value) => typeof value === "string" && value.trim().length > 0) ?? DEFAULT_THEME.tagline;
};

const resolveBrandName = (visualSpec, brandProfileSpec) => {
  if (visualSpec?.brand) return visualSpec.brand;
  if (typeof brandProfileSpec?.brand === "string") return brandProfileSpec.brand;
  if (brandProfileSpec?.brand?.name) return brandProfileSpec.brand.name;
  return DEFAULT_THEME.brandName;
};

/**
 * Select logo from visual spec, with fallback to brand table logo_url
 * @param {object} visualSpec - Visual spec with assets.logos array
 * @param {string} brandName - Brand name for alt text
 * @param {string|null} fallbackLogoUrl - Direct logo_url from brand table (fallback)
 */
const selectLogo = (visualSpec, brandName, fallbackLogoUrl = null) => {
  // 1. Try visual spec assets.logos array first
  if (Array.isArray(visualSpec?.assets?.logos)) {
    const logo = visualSpec.assets.logos.find((entry) => typeof entry?.uri === "string" && entry.uri.trim().length > 0);
    if (logo) {
      return {
        uri: logo.uri,
        alt: logo.name ?? `${brandName} logo`,
      };
    }
  }

  // 2. Fallback to brand.logo_url from profile spec visual section
  if (typeof fallbackLogoUrl === "string" && fallbackLogoUrl.trim().length > 0) {
    return {
      uri: fallbackLogoUrl,
      alt: `${brandName} logo`,
    };
  }

  // 3. Return default logo
  return DEFAULT_LOGO;
};

const resolveLogoFromSpec = (logoSpec, visualSpec, fallbackAlt) => {
  if (!logoSpec) return null;
  let uri = logoSpec.uri ?? null;
  if (!uri && logoSpec.asset_name && Array.isArray(visualSpec?.assets?.logos)) {
    const match = visualSpec.assets.logos.find(
      (entry) =>
        entry?.name?.toLowerCase() === logoSpec.asset_name.toLowerCase() &&
        typeof entry?.uri === "string"
    );
    if (match?.uri) {
      uri = match.uri;
    }
  }
  if (!uri) return null;
  return {
    uri,
    alt: logoSpec.alt ?? fallbackAlt,
    badge: logoSpec.badge ?? null,
  };
};

export const DEFAULT_BRAND_VISUAL_THEME = cloneTheme(DEFAULT_THEME);

/**
 * Extract fallback logo URL from brand profile spec
 * Checks: brandProfileSpec.visual?.logo_url, brandProfileSpec.brand_info?.visual?.logo_url
 */
const extractFallbackLogoUrl = (brandProfileSpec) => {
  const candidates = [
    brandProfileSpec?.visual?.logo_url,
    brandProfileSpec?.brand_info?.visual?.logo_url,
    brandProfileSpec?.logo_url,
  ];
  return candidates.find((url) => typeof url === "string" && url.trim().length > 0) ?? null;
};

/**
 * Extract fallback primary color from brand profile spec
 */
const extractFallbackPrimaryColor = (brandProfileSpec) => {
  const candidates = [
    brandProfileSpec?.visual?.primary_color,
    brandProfileSpec?.brand_info?.visual?.primary_color,
    brandProfileSpec?.primary_color,
  ];
  return candidates.find((c) => typeof c === "string" && c.trim().length > 0) ?? null;
};

export function buildBrandVisualTheme(visualSpec, brandProfileSpec) {
  const fallbackLogoUrl = extractFallbackLogoUrl(brandProfileSpec);
  const fallbackPrimaryColor = extractFallbackPrimaryColor(brandProfileSpec);

  if (!visualSpec) {
    const fallback = cloneTheme(DEFAULT_THEME);
    const brandName = resolveBrandName(null, brandProfileSpec);
    fallback.brandName = brandName;
    const fallbackTagline = buildTagline(null, brandProfileSpec);
    fallback.tagline = fallbackTagline;

    // Apply fallback logo if available
    if (fallbackLogoUrl) {
      const logo = { uri: fallbackLogoUrl, alt: `${brandName} logo` };
      fallback.logo = logo;
      fallback.logosByMode = { light: logo, dark: logo, noderiver: logo };
    }

    // Apply fallback primary color if available
    if (fallbackPrimaryColor) {
      fallback.palette.primary = fallbackPrimaryColor;
      fallback.palette.accent = fallbackPrimaryColor;
      fallback.modes.light.accent = fallbackPrimaryColor;
      fallback.modes.dark.accent = fallbackPrimaryColor;
      fallback.modes.noderiver.accent = fallbackPrimaryColor;
    }

    return fallback;
  }

  const theme = cloneTheme(DEFAULT_THEME);
  const palette = Array.isArray(visualSpec?.foundations?.colors?.palette)
    ? visualSpec.foundations.colors.palette
    : [];
  const gradients = Array.isArray(visualSpec?.foundations?.colors?.gradients)
    ? visualSpec.foundations.colors.gradients
    : [];
  const themeDefinitions = visualSpec?.foundations?.colors?.themes ?? {};

  const heroGradientConfig =
    gradients.find(
      (grad) =>
        /hero|wash|background/i.test(grad.token ?? "") || /hero|background/i.test(grad.usage ?? "")
    ) ?? gradients[0];

  const ctaGradientConfig =
    gradients.find(
      (grad) =>
        /cta|button|action|midnight/i.test(grad.token ?? "") ||
        /cta|action/i.test(grad.usage ?? "")
    ) ?? heroGradientConfig;

  const heroGradient =
    buildLinearGradient(heroGradientConfig?.stops, heroGradientConfig?.angle ?? 135) ??
    theme.gradients.hero;
  const ctaGradient =
    buildLinearGradient(ctaGradientConfig?.stops, ctaGradientConfig?.angle ?? 135) ?? heroGradient;

  const primary =
    pickPaletteColor(
      palette,
      (token, role) => token.includes("color.brand.primary") || role.includes("primary")
    ) ?? palette[0]?.value ?? theme.palette.primary;

  const accent =
    pickPaletteColor(
      palette,
      (token, role) =>
        token.includes("neon") ||
        token.includes("accent") ||
        role.includes("neon") ||
        role.includes("accent")
    ) ?? theme.palette.accent;

  const dark =
    pickPaletteColor(
      palette,
      (token, role) =>
        token.includes("deep") ||
        token.includes("shadow") ||
        role.includes("midnight") ||
        role.includes("shadow")
    ) ?? theme.palette.dark;

  const backgroundSemantic = buildPrioritySelector(palette, [
    (entry) => /cloud|surface|background|canvas|paper|wash|shell|mist/.test((entry.role ?? "").toLowerCase()),
    (entry) => /cloud|surface|background|canvas|paper|wash|shell|mist/.test((entry.token ?? "").toLowerCase()),
    (entry) =>
      (entry.token ?? "").toLowerCase().includes("neutral") &&
      !/(ink|text|copy)/.test((entry.role ?? "").toLowerCase()),
  ]);

  const neutral =
    ensureReadableSurface(
      backgroundSemantic ??
        pickPaletteColor(
          palette,
          (token, role) =>
            token.includes("neutral") ||
            token.includes("background") ||
            role.includes("cloud") ||
            role.includes("background")
        ) ??
        theme.palette.neutral
    );

  const ink =
    pickPaletteColor(
      palette,
      (token, role) =>
        token.includes("ink") ||
        token.includes("body") ||
        role.includes("copy") ||
        role.includes("text")
    ) ?? theme.palette.text;

  const brandName = resolveBrandName(visualSpec, brandProfileSpec);
  const tagline = buildTagline(visualSpec, brandProfileSpec);
  const logo = selectLogo(visualSpec, brandName, fallbackLogoUrl);

  theme.brandName = brandName;
  theme.tagline = tagline;
  theme.logo = logo;
  theme.gradients.hero = heroGradient;
  theme.gradients.cta = ctaGradient;
  theme.palette.primary = primary;
  theme.palette.accent = accent;
  theme.palette.dark = dark;
  theme.palette.neutral = neutral;
  theme.palette.text = ink;

  const mutedLight = applyAlpha(ink, 0.72);
  const borderLight = applyAlpha(ink, 0.14);

  const applyThemeOverrides = (modeKey, defaults, fallbackGradients) => {
    const def = themeDefinitions?.[modeKey];
    const next = {
      ...defaults,
      heroGradient: fallbackGradients.hero,
      ctaGradient: fallbackGradients.cta,
      logo: defaults.logo ?? null,
    };
    if (def) {
      next.background = def.background ?? next.background;
      next.surface = def.surface ?? next.surface;
      next.text = def.text ?? next.text;
      next.muted = def.muted ?? next.muted;
      next.border = def.border ?? next.border;
      next.input = def.input ?? next.input;
      next.accent = def.accent ?? next.accent;
      next.accentContrast = def.accent_contrast ?? next.accentContrast;
      next.heroGradient = def.hero_gradient ?? next.heroGradient;
      next.ctaGradient = def.cta_gradient ?? next.ctaGradient;
      const themedLogo = resolveLogoFromSpec(def.logo, visualSpec, `${brandName} logo`);
      if (themedLogo) {
        next.logo = themedLogo;
      }
    }
    return next;
  };

  const lightMode = applyThemeOverrides(
    "light",
    {
      ...theme.modes.light,
      background: ensureReadableSurface(neutral, theme.modes.light.background),
      surface: ensureReadableSurface(theme.modes.light.surface, "#ffffff"),
      text: ink ?? theme.modes.light.text,
      muted: mutedLight && mutedLight !== ink ? mutedLight : theme.modes.light.muted,
      border: borderLight && borderLight !== ink ? borderLight : theme.modes.light.border,
      input: ensureReadableSurface(theme.modes.light.input, "#ffffff"),
      accent: primary,
      accentContrast: "#ffffff",
      logo,
    },
    { hero: heroGradient, cta: ctaGradient }
  );

  const darkSurface = applyAlpha("#ffffff", 0.04);

  const darkMode = applyThemeOverrides(
    "dark",
    {
      ...theme.modes.dark,
      background: dark ?? theme.modes.dark.background,
      surface: darkSurface ?? theme.modes.dark.surface,
      accent: accent ?? theme.modes.dark.accent,
      accentContrast: theme.modes.dark.accentContrast,
      logo,
    },
    { hero: heroGradient, cta: ctaGradient }
  );

  const nodeRiverMode = {
    ...theme.modes.noderiver,
    background: heroGradient ?? theme.modes.noderiver.background,
    surface: darkMode.surface ?? theme.modes.noderiver.surface,
    text: darkMode.text ?? theme.modes.noderiver.text,
    muted: darkMode.muted ?? theme.modes.noderiver.muted,
    border: darkMode.border ?? theme.modes.noderiver.border,
    input: darkMode.input ?? theme.modes.noderiver.input,
    accent: darkMode.accent ?? accent ?? theme.modes.noderiver.accent,
    accentContrast: darkMode.accentContrast ?? theme.modes.noderiver.accentContrast,
    heroGradient: darkMode.heroGradient ?? heroGradient,
    ctaGradient: darkMode.ctaGradient ?? ctaGradient,
    logo: darkMode.logo ?? logo,
  };

  theme.modes.light = lightMode;
  theme.modes.dark = darkMode;
  theme.modes.noderiver = nodeRiverMode;
  theme.gradients.hero = lightMode.heroGradient ?? heroGradient;
  theme.gradients.cta = lightMode.ctaGradient ?? ctaGradient;

  const logosByMode = {
    light: lightMode.logo ?? logo,
    dark: darkMode.logo ?? logo,
    noderiver: nodeRiverMode.logo ?? darkMode.logo ?? logo,
  };

  theme.logosByMode = logosByMode;
  theme.logo = logosByMode.light ?? logosByMode.dark ?? logo;

  return theme;
}

